import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useController, useFieldArray, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { PiTrashBold } from 'react-icons/pi';

import { fetchMtrDetail } from '#/api/imsMgmt/settings/mtr/fetchDetail';
import { cn } from '#/lib/utils';
import { Button, DESTRUCTIVE_GHOST_BUTTON_CLASS } from '#/modules/commonComponents/button';
import ListFieldMobileLabel from '#/modules/commonComponents/form/ListFieldMobileLabel';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import { SelectCore } from '#/modules/commonComponents/form/select';
import { materialUnitOptionsQueryKey } from '#/modules/imsMgmt/helper/materialUnit/materialUnitQueryKeys';
import { useMaterialUnitOptions } from '#/modules/imsMgmt/helper/materialUnit/useMaterialUnitOptions';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import InvStockPickerDialog from '#/modules/imsMgmt/receipts/stockTransfer/components/InvStockPickerDialog';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { mapInvStockRowToRepairMaterial } from '#/modules/momMgmt/workOrder/helper/workOrderRepair';
import { formatDisplayQty } from '#/utils/formatNumber';
import { lookupQueryKey } from '#/utils/queryKeys';

/** 物料｜倉別｜單位｜數量｜移除 */
const LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[minmax(12rem,1fr)_minmax(8rem,12rem)_minmax(7rem,9rem)_minmax(6rem,8rem)_auto]';

const LINE_HEADER_CLASS = cn(
  'hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-2 px-3 py-2.5',
  '@3xl/form-section:items-center',
  LINE_GRID_COLS,
);

/** 既有用料只有 mtrId：與單位選項共用 /mtr/{id} 快取取名稱。 */
function useMtrLabel(mtrId, fallback) {
  const query = useQuery({
    queryKey: materialUnitOptionsQueryKey(mtrId),
    queryFn: () => fetchMtrDetail(mtrId),
    enabled: Boolean(mtrId) && !fallback?.mtrName,
  });
  const detail = query.data?.data;
  return {
    name: fallback?.mtrName || detail?.name || '',
    code: fallback?.mtrNo || detail?.mtrNo || '',
  };
}

const MaterialLineRow = ({
  index,
  control,
  register,
  errors,
  setValue,
  onRemove,
  warehouseLabelById,
  readOnly,
}) => {
  const line = useWatch({ control, name: `materials.${index}` }) ?? {};
  const unitIdField = `materials.${index}.unitId`;
  const mtrId = String(line.mtrId ?? '').trim();
  const mtr = useMtrLabel(mtrId, line);
  const unitError = getNestedError(errors, unitIdField);

  const {
    selectOptions: unitSelectOptions,
    defaultUnitId,
    isLoading: unitOptionsLoading,
  } = useMaterialUnitOptions({
    mtrId,
    initialOptions: Array.isArray(line.unitOptions) && line.unitOptions.length > 0
      ? line.unitOptions
      : null,
    enabled: Boolean(mtrId),
  });

  const { field: unitField } = useController({ name: unitIdField, control, defaultValue: '' });

  useEffect(() => {
    if (readOnly || !defaultUnitId || unitOptionsLoading || String(line.unitId ?? '').trim()) {
      return;
    }
    setValue(unitIdField, defaultUnitId, { shouldDirty: true, shouldValidate: false });
  }, [readOnly, defaultUnitId, unitOptionsLoading, line.unitId, unitIdField, setValue]);

  const warehouseLabel = line.warehouseLabel
    || warehouseLabelById.get(line.warehouseId)
    || '—';
  const availableHint = line.availableQty != null
    ? `可用 ${formatDisplayQty(line.availableQty)}（基本單位）`
    : null;

  return (
    <li className={LINE_ROW_CLASS}>
      <div className="min-w-0">
        <MaterialNameStack name={mtr.name} code={line.stockNo || mtr.code} />
        {availableHint ? <p className="text-xs text-muted-foreground">{availableHint}</p> : null}
      </div>
      <p className="truncate text-sm text-gray-800" title={warehouseLabel}>{warehouseLabel}</p>
      <div className="min-w-0">
        <ListFieldMobileLabel hasError={Boolean(unitError)}>單位</ListFieldMobileLabel>
        <SelectCore
          id={unitIdField}
          options={unitSelectOptions}
          value={unitField.value}
          onChange={unitField.onChange}
          onBlur={unitField.onBlur}
          isDisabled={readOnly || !mtrId || unitOptionsLoading}
          placeholder="單位"
          noOptionsMessage="此物料尚無可選單位"
          errors={errors}
        />
      </div>
      <PlainRegisteredInput
        id={`materials.${index}.qty`}
        register={register}
        errors={errors}
        type="number"
        min={0}
        step="any"
        placeholder="用量"
        mobileLabel="數量"
        ariaLabel={`用料數量 ${index + 1}`}
        readOnly={readOnly}
      />
      <div className="flex justify-end">
        {!readOnly ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
            aria-label={`移除用料 ${index + 1}`}
            onClick={() => onRemove(index)}
          >
            <PiTrashBold className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>
    </li>
  );
};

MaterialLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  warehouseLabelById: PropTypes.instanceOf(Map).isRequired,
  readOnly: PropTypes.bool,
};

/**
 * 維修單用料：可從任一倉的庫存取料（不限工單工作倉）；結單時才扣帳。
 * 同一庫存可多列，後端合計檢查可用量。
 */
const WorkOrderRepairMaterialsEditor = ({
  control,
  register,
  errors,
  setValue,
  readOnly = false,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'materials',
    keyName: 'fieldKey',
  });

  const warehouseOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });
  const warehouseLabelById = new Map(
    (warehouseOptionsQuery.data ?? []).map((option) => [option.value, option.label]),
  );

  function handlePickerConfirm(selectedRows) {
    (selectedRows ?? [])
      .map(mapInvStockRowToRepairMaterial)
      .filter(Boolean)
      .forEach((line) => append(line));
  }

  return (
    <div className="form-section @container/form-section w-full">
      <div className="form-section__header">
        <h2 className="form-block__title">用料</h2>
        {!readOnly ? (
          <Button type="button" size="sm" onClick={() => setPickerOpen(true)}>
            選擇庫存
          </Button>
        ) : null}
      </div>
      {errors?.materials?.message ? (
        <p className="mt-2 text-sm text-red-500">{errors.materials.message}</p>
      ) : null}
      {fields.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">無用料</p>
      ) : (
        <div>
          <div className={LINE_HEADER_CLASS} aria-hidden>
            <p>物料／庫存</p>
            <p>倉別</p>
            <p>單位</p>
            <p>數量</p>
            <p />
          </div>
          <ul className="m-0 list-none divide-y divide-gray-200 p-0">
            {fields.map((field, index) => (
              <MaterialLineRow
                key={field.fieldKey}
                index={index}
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                onRemove={remove}
                warehouseLabelById={warehouseLabelById}
                readOnly={readOnly}
              />
            ))}
          </ul>
        </div>
      )}

      {!readOnly ? (
        <InvStockPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onConfirm={handlePickerConfirm}
          title="選擇維修用料庫存"
          description="可從任一倉別取料；結單時才扣帳。"
        />
      ) : null}
    </div>
  );
};

WorkOrderRepairMaterialsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

export default WorkOrderRepairMaterialsEditor;
